import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Link } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { APP_STORE_URL, GOOGLE_PLAY_URL, openStoreUrl } from '@/components/PublicFooter/publicFooterStoreLinks';
import { PrimaryCta, useInteractive, useLanding } from './LandingKit';
import LandingHeroComposer from './LandingHeroComposer';
import PageVideo from '@/components/Tutorials/PageVideo';
import LivingPixelScene from './LivingPixelScene';
import ReferralAppHandoff from './ReferralAppHandoff';
import { content } from '@/lib/landingPageSeo';
import { LAYOUT, fluid, isWeb } from './landingTokens';

const PLATFORMS = [
  { label: 'Mac', short: 'Mac', icon: 'laptop', href: '/mac' },
  { label: 'iPhone & iPad', short: 'iPhone', icon: 'mobile-screen-button', url: APP_STORE_URL, mobile: true },
  { label: 'Android', short: 'Android', icon: 'mobile-screen-button', url: GOOGLE_PLAY_URL, mobile: true },
  { label: 'Chrome & Brave', short: 'Chrome', icon: 'globe', href: '/extension' },
];

// Marketing pages never navigate away on their own; the end card waits for a click.
const HERO_VIDEO_NEXT = { label: 'Learn to use AI with free lessons', href: '/learn', autoplay: false, action: 'Learn about AI' };
const HERO_VIDEO_SIGNUP = { label: 'Create a free account', href: '/signup', title: 'Put AI to work for you.' };
const VISUALLY_HIDDEN = { position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 };

/** Illustrated scenery and independent atmosphere layers give the hero depth. */
function HeroAtmosphere() {
  const { isDark, width } = useLanding();
  const stackedScene = width < 1100;
  const sky = isDark ? '#111A17' : '#DCEEF0';
  const skyClear = isDark ? 'rgba(17,26,23,0)' : 'rgba(220,238,240,0)';
  return (
    <View pointerEvents="none" aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={[StyleSheet.absoluteFillObject, { backgroundColor: sky }]}>
      <View style={{ position: 'absolute', left: -24, right: -24, bottom: -12, height: stackedScene ? 660 : '104%' }}>
        <LivingPixelScene />
        {stackedScene && <LinearGradient colors={[sky, skyClear]} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 240 }} />}
      </View>
      <LinearGradient
        colors={isDark ? ['rgba(9,20,16,.88)', 'rgba(9,20,16,.7)', 'rgba(9,20,16,.25)'] : ['rgba(235,247,245,.38)', 'rgba(235,247,245,.18)', 'rgba(255,250,239,0)']}
        locations={[0, 0.48, 1]}
        style={StyleSheet.absoluteFillObject}
      />
      <LinearGradient colors={isDark ? ['rgba(21,29,24,0)', '#151D18'] : ['rgba(240,243,236,0)', '#F0F3EC']} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: stackedScene ? 60 : 120 }} />
    </View>
  );
}

function PlatformLink({ platform }) {
  const { c, isXS } = useLanding();
  const { hovered, handlers } = useInteractive();
  const color = hovered ? c.ink : c.inkSoft;
  const link = (
    <Pressable
      {...handlers}
      onPress={platform.url ? () => openStoreUrl(platform.url) : undefined}
      accessibilityRole="link"
      accessibilityLabel={`Gab AI for ${platform.label}`}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 32, ...(isWeb ? { cursor: 'pointer' } : {}) }}
    >
      <Icon name={platform.icon} size={12} color={color} />
      <Text weight="medium" style={{ color, fontSize: 13, ...(isWeb ? { textDecorationLine: hovered ? 'underline' : 'none' } : {}) }}>{isXS ? platform.short : platform.label}</Text>
    </Pressable>
  );
  return platform.href ? <Link href={platform.href} asChild>{link}</Link> : link;
}

function HeroPlatforms() {
  const { c, isXS } = useLanding();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: isXS ? 13 : 18, rowGap: 2 }}>
      <Text weight="medium" style={{ color: c.ink, fontSize: 13 }}>Download our apps</Text>
      {PLATFORMS.filter(platform => !isXS || platform.mobile).map(platform => <PlatformLink key={platform.label} platform={platform} />)}
    </View>
  );
}

function LandingEditorialHero({ topInset = 0 }) {
  const { c, width, isXS, pad, display } = useLanding();
  const headline = display({ min: 46, max: 104, leading: 1.04, tracking: -0.055 });

  return (
    <View accessibilityLabel="Gab AI" style={{ width: '100%', alignItems: 'center', backgroundColor: c.page, position: 'relative', overflow: 'hidden' }}>
      <HeroAtmosphere />
      <View style={{ width: '100%', maxWidth: LAYOUT.max, paddingHorizontal: pad, paddingTop: topInset + (isWeb ? fluid(width, 88, 112) : fluid(width, 112, 160)), paddingBottom: width < 1100 ? 340 : 150, alignItems: 'center' }}>
        <Text tag="h1" weight="extraBold" style={isWeb ? VISUALLY_HIDDEN : { color: c.ink, ...headline, maxWidth: '100%', textAlign: 'center' }}>
          {content.headline}
        </Text>

        {isWeb
          ? <View style={{ width: '100%', maxWidth: 780 }}><PageVideo page="home" next={HERO_VIDEO_NEXT} signup={HERO_VIDEO_SIGNUP} playButton className="page-video page-video-hero" /></View>
          : <LandingHeroComposer style={{ marginTop: fluid(width, 36, 48) }} />}

        <View style={{ alignItems: 'center', gap: isXS ? 8 : 10, marginTop: fluid(width, 22, 28), paddingVertical: isXS ? 10 : 8, paddingHorizontal: isXS ? 14 : 22, width: '100%', maxWidth: 680, borderRadius: 18, borderWidth: 1, borderColor: c.rule, backgroundColor: c.isDark ? 'rgba(16,25,20,.86)' : 'rgba(255,252,245,.88)', ...(isWeb ? { backdropFilter: 'blur(12px)' } : {}) }}>
          <HeroPlatforms />
          <ReferralAppHandoff />
        </View>
        <View style={{ marginTop: fluid(width, 20, 26), alignItems: 'center', width: isXS ? '100%' : undefined, maxWidth: 680 }}>
          <PrimaryCta label="Sign up for free" full={isXS} />
        </View>
      </View>
    </View>
  );
}

export default React.memo(LandingEditorialHero);
